"use client";

import React from 'react';
import { LOGO_URLS } from '@/utils/constants';

interface GradientLogoProps {
  /** Use full logo or badge (icon) version */
  variant?: 'full' | 'badge';
  /** Width in pixels */
  width?: number;
  /** Height in pixels */
  height?: number;
  /** Additional CSS classes */
  className?: string;
  /** Whether to use gradient (true) or original colors (false) */
  useGradient?: boolean;
}

/**
 * CheerVue logo component that can display with the brand gradient
 * Uses CSS mask to overlay the gradient on the SVG shape
 */
export default function GradientLogo({
  variant = 'full',
  width,
  height,
  className = '',
  useGradient = true,
}: GradientLogoProps) {
  // Default dimensions based on variant
  const defaultWidth = variant === 'full' ? 150 : 32;
  const defaultHeight = variant === 'full' ? 40 : 32;
  
  const finalWidth = width || defaultWidth;
  const finalHeight = height || defaultHeight;
  
  const logoUrl = variant === 'full' ? LOGO_URLS.FULL_LOGO : LOGO_URLS.BADGE_LOGO;

  if (!useGradient) {
    // Return regular image if gradient not needed
    return (
      <img
        src={logoUrl}
        alt="CheerVue Logo"
        width={finalWidth}
        height={finalHeight}
        className={className}
      />
    );
  }

  return (
    <div
      className={`gradient-logo ${className}`}
      style={{
        width: finalWidth,
        height: finalHeight,
        background: 'var(--logo-gradient, linear-gradient(to right, var(--color-brand-gradient-start), var(--color-brand-gradient-end)))',
        maskImage: `url(${logoUrl})`,
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
        maskSize: 'contain',
        WebkitMaskImage: `url(${logoUrl})`,
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        WebkitMaskSize: 'contain',
      }}
      role="img"
      aria-label="CheerVue Logo"
    />
  );
}

